import { FCX } from "@models";
import { UseFormReturn } from "@hooks/useForm";
import {
    BodyUpdateSurvey,
    GetSurveyDefaultAgeGroupsResponse,
    GetSurveyDefaultDemographicResponse,
    GetSurveyInitialDataResponse,
} from '@core/api/useSurveys';
import { useCallback, useMemo } from "react";
import { equalInLC } from "@core/utils/string";
import Options, { OptionToRender } from "@pages/SurveyDetails/components/Options";
import ManagementSectionHeader from "@pages/SurveyDetails/components/ManagementSectionHeader";

const DemographicManagement: FCX<{
    form: UseFormReturn<BodyUpdateSurvey>;
    initialData: GetSurveyInitialDataResponse;
    defaultAgeGroups: GetSurveyDefaultDemographicResponse;
    defaultDemographicGroups: GetSurveyDefaultAgeGroupsResponse;
    isOpened: boolean;
    setIsOpened: (_value: boolean) => void;
}> = ({
    form: {
        state: {
            demographicGroups,
            ageGroups,
        },
        actions,
    },
    initialData: {
        ageGenerations,
        genders,
        ethnicities,
    },
    defaultAgeGroups,
    defaultDemographicGroups,
    isOpened,
    setIsOpened,
}) => {
    const getGenderEthnicityOptions = useCallback(
        (genderName: string) => {
            const genderOption = genders?.find(o => equalInLC(o.name ?? '', genderName));

            const genderGroups = demographicGroups
                ?.filter(o => o.gender === genderOption?.id)
                ?.map(o => ({
                    ...o,
                    ...(ethnicities?.find(oo => oo.id === o.ethnicity))
                }));

            const genderTotal = genderGroups?.reduce(
                (acc, o) => acc + (o.percentage ?? 0),
                0
            );

            return {
                totalOption: {
                    id: genderOption?.id,
                    title: genderOption?.name ?? '',
                    value: +(genderTotal ?? 0).toFixed(1),
                },
                options: (genderGroups ?? []).map(
                    o => ({
                        id: `${genderOption?.id}-${o.id}`,
                        title: `${o.name} / ${genderOption?.name}`,
                        value: o.percentage ?? 0,
                        genderId: o?.gender,
                        ethnicityId: o.ethnicity,
                    })
                ),
            };
        },
        [genders, demographicGroups, ethnicities]
    );

    const ageOptions = useMemo(
        () => {
            const ageOptions = ageGroups?.map(o => ({
                ...o,
                ...(ageGenerations?.find(oo => oo?.id === o.ageGeneration)),
            }));

            const totalAge = ageOptions?.reduce(
                (acc, o) => acc + (o.percentage ?? 0),
                0
            );

            return {
                totalOption: {
                    id: 'total-age',
                    title: 'Generation',
                    value: +(totalAge ?? 0).toFixed(1),
                },
                options: (ageOptions ?? []).map(o => ({
                    id: o.ageGeneration ?? 0,
                    title: o.name ?? '',
                    value: o.percentage ?? 0,
                })),
            };
        },
        [ageGenerations, ageGroups]
    );

    const {
        options: maleOptions,
        totalOption: totalMaleOption,
    } = useMemo(
        () => getGenderEthnicityOptions('male'),
        [getGenderEthnicityOptions]
    );

    const {
        options: femaleOptions,
        totalOption: totalFemaleOption,
    } = useMemo(
        () => getGenderEthnicityOptions('female'),
        [getGenderEthnicityOptions]
    );

    const onGenderGenerationChange = useCallback(
        (value: string, id: OptionToRender["id"]) => {
            const option = [
                ...maleOptions,
                ...femaleOptions,
            ].find(o => o.id === id);

            actions.setField('demographicGroups', demographicGroups?.map(o => ({
                ...o,
                percentage: option?.genderId === o.gender && option?.ethnicityId === o.ethnicity ? +value : o.percentage,
            })));
        },
        [actions, demographicGroups, maleOptions, femaleOptions]
    );

    const reset = useCallback(
        () => {
            actions.setFields({
                demographicGroups: defaultDemographicGroups,
                ageGroups: defaultAgeGroups,
            });
        },
        [actions, defaultDemographicGroups, defaultAgeGroups]
    );

    return (
        <>
            <div className="Separator"/>
            <ManagementSectionHeader
                heading="Demographic management"
                reset={reset}
                isOpened={isOpened}
                setIsOpened={setIsOpened}
            />
            {isOpened && (
                <div className="SurveyDetails__fielding-container">
                    <Options
                        data={maleOptions}
                        onItemChange={onGenderGenerationChange}
                        title={(
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: totalMaleOption.value + totalFemaleOption.value === 100 ? undefined : 'red',
                            }}>
                                <div>{totalMaleOption.title}</div>
                                <div>{totalMaleOption.value}%</div>
                            </div>
                        )}
                    />
                    <div className="SurveyDetails__spacer"/>
                    <Options
                        data={femaleOptions}
                        onItemChange={onGenderGenerationChange}
                        title={(
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: totalMaleOption.value + totalFemaleOption.value === 100 ? undefined : 'red',
                            }}>
                                <div>{totalFemaleOption.title}</div>
                                <div>{totalFemaleOption.value}%</div>
                            </div>
                        )}
                    />
                    <div className="SurveyDetails__spacer"/>
                    <Options
                        data={ageOptions.options}
                        onItemChange={(value, id) => {
                            actions.setField('ageGroups', ageGroups?.map(o => ({
                                ...o,
                                percentage: id === o.ageGeneration ? +value : o.percentage,
                            })));
                        }}
                        title={(
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: ageOptions.totalOption.value === 100 ? undefined : 'red',
                            }}>
                                <div>{ageOptions.totalOption.title}</div>
                                {ageOptions.totalOption.value !== 100 && <div>{ageOptions.totalOption.value}%</div>}
                            </div>
                        )}
                    />
                </div>
            )}
        </>
    );
}

export default DemographicManagement;